import React, { useState, useEffect } from 'react';
import { securePut, secureGet, securePost } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useHistory } from 'react-router-dom';
import { formatAmount } from "../../utils/format";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useLocation } from 'react-router-dom';
import { PrimaryButton } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';
import GeneratePopup from "../../components/GeneratePopUp";
import { PAYMENT_ENUMS } from "../../utils/paymentModes";
import discountIcon from '../../icons/discount.svg';

import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import plusY from '../../icons/plusY.svg';
import minusY from '../../icons/minusY.svg';
import noImage from '../../icons/noImageIcon.svg';
import EyeIcon from '../../icons/eyeB.svg';
import xIcon from '../../icons/x.svg';

const QuoteForm = ({
    title,
    baseFields = [],
    listFields = [],
    products = [],
    services = [],
    typeOptions = [],
    onSuccess,
    onCancel,
    initialValues = {},
    onRevisionChange, // Callback to expose revision change handler
}) => {
    const [formValues, setFormValues] = useState({});
    const [listEntities, setListEntities] = useState([]);
    const [currentEntity, setCurrentEntity] = useState({});
    const [totalHT, setTotalHT] = useState(0);
    const [totalTTC, setTotalTTC] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const id = location.state?.id;
    const [quote, setQuote] = useState(null);


    const [isPopupOpen, setPopupOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingPrice, setEditingPrice] = useState(null); // { index, value: string } when user is typing in a price input

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    useEffect(() => {
        const checkDataReadiness = () => {
            const baseFieldsReady = baseFields.every(field =>
                !field.options || field.options.length > 0
            );

            if (id) {
                return quote && baseFieldsReady;
            }
            return baseFieldsReady;
        };

        if (checkDataReadiness()) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [quote, baseFields, id]);

    useEffect(() => {
        // If initialValues contains a quote object (has id and quote-specific fields), use it directly
        if (initialValues && initialValues.id && (initialValues.details !== undefined || initialValues.client !== undefined || initialValues.quoteNumber !== undefined)) {
            setQuote(initialValues);
            console.log("quote", initialValues)

            return;
        }

        // Otherwise, fetch quote using id from location.state
        const abortController = new AbortController();
        const fetchQuote = async () => {
            if (!id) return;
            try {
                // Build URL with revisionId if provided
                let url = `/quotes/${id}`;
                const response = await secureGet(url, showSnackbar, { signal: abortController.signal });
                setQuote(response.data);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Failed to fetch quote:", error);
                }
            }
        };

        fetchQuote();
        return () => {
            abortController.abort();
        };
    }, [id, initialValues]);

    useEffect(() => {
        if (quote) {
            // Store clientId from initialValues for payload (always prefer initialValues.clientId)
            // Store clientName from initialValues for display in text field
            const formInitialValues = {
                clientId: initialValues?.clientId || quote.client?.id,
                clientName: initialValues?.clientName || quote.client?.name || "",
                repoId: quote.repo?.id || "",
                advancePayment: quote.advancePayment || 0,
            };
            setFormValues(formInitialValues);


            const productList = quote.details?.map((detail) => {
                return {
                    detailId: detail.id,
                    product: detail.product,
                    quantity: detail.quantity,
                    discount: detail.salesDiscount || 0,
                    ...(detail.product == null ? {
                        designation: detail.designation || "",
                        unitPrice: detail.unitPrice != null ? parseFloat(detail.unitPrice) : null,
                        vatRate: detail.vatRate != null ? parseFloat(detail.vatRate) : null,
                    } : {}),
                };
            }) || [];
            setListEntities(productList);
        } else if (initialValues && initialValues.receptionDocumentId && !quote) {
            // For new quotes, set receptionDocumentId, clientId (for payload), and clientName (for display)
            setFormValues((prev) => ({
                ...prev,
                receptionDocumentId: initialValues.receptionDocumentId,
                clientId: initialValues.clientId || prev.clientId, // For payload
                clientName: initialValues.clientName || prev.clientName || "", // For display
            }));
        } else if (initialValues?.clientId && !quote) {
            // Always pre-fill clientId (for payload) and clientName (for display) from initialValues
            setFormValues((prev) => ({
                ...prev,
                clientId: initialValues.clientId, // For payload
                clientName: initialValues.clientName || "", // For display
            }));
        }
    }, [quote, initialValues]);

    useEffect(() => {
        const calculatedTotalHT = listEntities.reduce(
            (sum, entity) => {
                const unitPriceHT = (entity.unitPrice != null && entity.unitPrice !== undefined)
                    ? entity.unitPrice
                    : (entity.product?.priceHT ?? 0);
                const productDiscount = entity.product?.discount || 0;
                const quoteDiscount = entity.discount || 0;
                const priceAfterProductDiscount = unitPriceHT * (1 - productDiscount / 100);
                const finalPrice = priceAfterProductDiscount * (1 - quoteDiscount / 100);
                return sum + entity.quantity * finalPrice;
            },
            0
        );

        const calculatedTotalTTC = listEntities.reduce(
            (sum, entity) => {
                const unitPriceHT = (entity.unitPrice != null && entity.unitPrice !== undefined)
                    ? entity.unitPrice
                    : (entity.product?.priceHT ?? 0);
                const isProductLine = isProductEntity(entity);
                const vatRate = isProductLine && (entity.vatRate != null && entity.vatRate !== undefined)
                    ? entity.vatRate / 100
                    : 0.20;
                const unitPriceTTC = unitPriceHT * (1 + vatRate);
                const productDiscount = entity.product?.discount || 0;
                const quoteDiscount = entity.discount || 0;
                const priceAfterProductDiscount = unitPriceTTC * (1 - productDiscount / 100);
                const finalPrice = priceAfterProductDiscount * (1 - quoteDiscount / 100);
                return sum + entity.quantity * finalPrice;
            },
            0
        );

        setTotalHT(calculatedTotalHT.toFixed(2));
        setTotalTTC(calculatedTotalTTC.toFixed(2));
    }, [listEntities]);

    const handleInputChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const removeListEntity = (index) => {
        setListEntities((prevEntities) =>
            prevEntities.filter((_, i) => i !== index)
        );
    };

    // Check if an entity is a service (not a product)
    const isService = (entity) => {
        return entity?.product?.family?.familyType === "SERVICE";
    };

    // Check if an entity is a product line (free line: has designation, unitPrice, vatRate)
    const isProductEntity = (entity) => {
        return (entity.unitPrice != null || entity.designation != null) || (entity.product?.family?.familyType !== "SERVICE");
    };

    const handleTrashClick = (index) => {
        removeListEntity(index);
    };

    const handleDiscountChange = (index, change) => {
        setListEntities((prevEntities) =>
            prevEntities.map((entity, i) => {
                if (i === index) {
                    return {
                        ...entity,
                        discount: Math.max(0,
                            parseFloat((entity.discount + change).toFixed(1))
                        )
                    };
                }
                return entity;
            })
        );
    };

    const handleUnitPriceChange = (index, value, discountInfo) => {
        const normalized = String(value).replace(',', '.');
        const num = normalized === '' ? null : parseFloat(normalized);
        if (num !== null && (Number.isNaN(num) || num < 0)) return;
        setListEntities((prevEntities) =>
            prevEntities.map((entity, i) => {
                if (i !== index) return entity;
                let unitPrice = num;
                if (num !== null && discountInfo) {
                    const { productDiscount = 0, quoteDiscount = 0 } = discountInfo;
                    const multiplier = (1 - productDiscount / 100) * (1 - quoteDiscount / 100);
                    unitPrice = multiplier > 0 ? num / multiplier : num;
                }
                return { ...entity, unitPrice: unitPrice ?? undefined };
            })
        );
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // In modify mode, listFields are not required if listEntities already exist
        const fieldsToValidate = listEntities.length > 0 
            ? [...baseFields] 
            : [...baseFields, ...listFields];
        
        const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
        if (missing.length > 0) {
            showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
            return;
        }
        
        // Build payload according to backend format (removing items = new version only, no credit note)
        const currentDetails = listEntities.map((entity) => {
            const isProductLine = isProductEntity(entity);
            return {
                id: entity.detailId ? parseInt(entity.detailId, 10) : null,
                quantity: parseInt(entity.quantity, 10) || 0,
                salesDiscount: parseFloat(entity.discount || 0),
                freeLine: isProductLine,
                productId: isProductLine ? null : (parseInt(entity.product?.id, 10) || null),
                unitPrice: parseFloat(entity.unitPrice ?? entity.product?.priceHT ?? 0),
                vatRate: parseFloat(isProductLine ? (entity.vatRate ?? 0) : 20),
                designation: (entity.designation != null && entity.designation !== "") ? String(entity.designation) : "",
            };
        }).filter(detail => detail.freeLine || detail.productId > 0);

        const payload = {
            clientId: parseInt(formValues.clientId, 10) || 0,
            advancePayment: parseFloat(formValues.advancePayment || 0),
            title: formValues.title || "",
            ...(formValues.repoId && { repoId: parseInt(formValues.repoId, 10) }),
            ...(initialValues?.receptionDocumentId && { receptionDocumentId: parseInt(initialValues.receptionDocumentId, 10) }),
            ...(quote?.id && { quoteId: parseInt(quote.id, 10) }),
            details: currentDetails,
        };


        try {
            setSubmitting(true);
            console.log("Submitting quote with payload:", payload);
            await securePut("/quotes/add", payload, showSnackbar);
            onSuccess();
            setFormValues({});
            setListEntities([]);
        } catch (error) {
            console.error("Failed to submit quote:", error);
        } finally {
            setSubmitting(false);
        }
    };



    const addListEntity = () => {
        const { type, product, quantity, designation, unitPrice, vatRate } = currentEntity;
        if (!type) {
            showSnackbar(t('placeholder.empty_fields'), "error");
            return;
        }

        const isProductType = type === 'products';
        const isServiceType = type === 'services';

        if (isProductType) {
            if (designation == null || designation === '' || quantity == null || quantity <= 0 ||
                unitPrice == null || unitPrice === '' || vatRate == null) {
                showSnackbar(t('placeholder.empty_fields'), "error");
                return;
            }
        } else if (isServiceType) {
            if (!product || !quantity || quantity <= 0) {
                showSnackbar(t('placeholder.empty_fields'), "error");
                return;
            }
        } else {
            return;
        }

        if (isServiceType) {
            const selectedItem = services.find((p) => p.value === product);
            if (!selectedItem) {
                showSnackbar('Service introuvable', "error");
                return;
            }
            const productId = selectedItem.fullEntity?.id;
            const newQuantity = parseInt(quantity, 10);
            const existingIndex = listEntities.findIndex((entity) => entity.product?.id === productId);

            if (existingIndex >= 0) {
                const existingEntity = listEntities[existingIndex];
                setListEntities((prev) => {
                    const newEntities = [...prev];
                    newEntities[existingIndex] = {
                        ...existingEntity,
                        quantity: existingEntity.quantity + newQuantity,
                    };
                    return newEntities;
                });
            } else {
                setListEntities((prev) => [...prev, {
                    product: selectedItem.fullEntity,
                    quantity: newQuantity,
                    discount: 0,
                }]);
            }
        } else {
            const newQuantity = parseInt(quantity, 10);
            const newEntity = {
                product: null,
                quantity: newQuantity,
                discount: 0,
                designation: designation ?? "",
                unitPrice: unitPrice != null && unitPrice !== '' ? parseFloat(unitPrice) : null,
                vatRate: vatRate != null && vatRate !== '' ? parseFloat(vatRate) : 0,
            };
            setListEntities((prev) => [...prev, newEntity]);
        }

        setCurrentEntity({});
    };


    const handleImageError = (e) => {
        e.target.src = noImage;
        e.target.style.width = "25px";
        e.target.style.height = "25px";
        e.target.style.objectFit = "contain";
    };

    console.log("listEntities", listEntities);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
            <div className="bg-white rounded-[12px] shadow-lg w-[75%] h-[760px] flex flex-col">
                <Helmet>
                    <title>Drivnest - {t("titles.quotes")}</title>
                </Helmet>

                {/* Header with title and close button */}
                <div className="p-6 pb-0">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
                    </div>
                </div>
                
                {/* Content */}
                <div className="px-8 overflow-hidden flex-1 flex flex-col">
                    {(
                <form className="flex flex-col h-full">
                    {/* Base Fields - Fixed */}
                    <div className="mb-4">
                        <div className="grid grid-cols-3 gap-4">
                            {baseFields.map((field, index) => {
                                // For clientId field (text field), display clientName but payload uses clientId
                                let displayValue = formValues[field.name] || "";
                                if (field.name === 'clientId') {
                                    // Display clientName from initialValues or formValues
                                    displayValue = formValues.clientName || initialValues?.clientName || "";
                                }
                                
                                return (
                                    <div key={index}>
                                        <Input
                                            type={field.type || "text"}
                                            label={field.label}
                                            name={field.name}
                                            value={displayValue}
                                            options={field.options || []}
                                            required={field.required}
                                                    disabled={field.name === 'clientId'}
                                            onChange={(value) => {
                                                if (field.name === 'clientId') {
                                                    // Update clientName for display, but keep clientId from initialValues for payload
                                                    setFormValues(prev => ({
                                                        ...prev,
                                                        clientName: value,
                                                        // Always preserve clientId from initialValues for payload
                                                        clientId: initialValues?.clientId || prev.clientId,
                                                    }));
                                                } else {
                                                    handleInputChange(field.name, value);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {errorMessage && (
                            <p className="mt-2 text-sm text-red-500 font-semibold">{errorMessage}</p>
                        )}
                    </div>

                    {/* List Fields - Fixed */}
                    {listFields.length > 0 && (
                        <div className="mb-4">
                            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.products`)}</h3>
                                    <div className="flex items-end gap-3 mt-2">
                                        <div className="grid grid-cols-5 gap-4 flex-1">
                                {listFields
                                    .filter((field) => !field.showCondition || currentEntity.type === field.showCondition)
                                    .map((field, index) => {
                                    let fieldLabel = field.label;
                                    if (field.name === 'product') {
                                        fieldLabel = currentEntity.type === 'services' 
                                            ? t(`fields.list.service`) 
                                            : t(`fields.list.product`);
                                    }
                                    
                                    return (
                                        <Input
                                            key={index}
                                            type={field.type || 'text'}
                                            label={fieldLabel}
                                            name={field.name}
                                            value={currentEntity[field.name] || ''}
                                            required={field.required}
                                            onChange={(value) => {
                                                if (field.name === 'type') {
                                                    setCurrentEntity({
                                                        ...currentEntity,
                                                        type: value,
                                                        product: '',
                                                    });
                                                } else {
                                                    setCurrentEntity((prev) => ({ ...prev, [field.name]: value }));
                                                }
                                            }}
                                            options={field.name === 'product'
                                                ? (currentEntity.type === 'services' ? services : products).map((item) => ({
                                                    value: item.value,
                                                    name: item.name,
                                                    reference: item.reference,
                                                    fullEntity: item.fullEntity,
                                                }))
                                                : field.name === 'type'
                                                ? typeOptions
                                                : field.options || []}
                                        />
                                    );
                                })}
                                        </div>
                                <button
                                    type="button"
                                    onClick={addListEntity}
                                    className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
                                >
                                    <img src={plusIcon} alt="Add" className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* List Entity List - Scrollable */}
                    {listEntities.length > 0  && (
                        <div className="flex-1 overflow-y-auto space-y-[8px]">
                            {listEntities.map((entity, index) => {
                                const isProductLine = isProductEntity(entity);
                                const unitPriceHT = (entity.unitPrice != null && entity.unitPrice !== undefined)
                                    ? entity.unitPrice
                                    : (entity.product?.priceHT ?? 0);
                                // Services: 20% TVA by default; free line: value from input field
                                const displayVatRate = isProductLine ? (entity.vatRate ?? 0) : 20;
                                const taxRate = isProductLine
                                    ? (entity.vatRate != null && entity.vatRate !== undefined ? entity.vatRate / 100 : 0)
                                    : 0.20;
                                const unitPriceTTC = unitPriceHT * (1 + taxRate);

                                const productDiscount = entity.product?.discount || 0;
                                const quoteDiscount = entity.discount || 0;

                                const priceAfterProductDiscountHT = unitPriceHT * (1 - productDiscount / 100);
                                const priceAfterProductDiscountTTC = unitPriceTTC * (1 - productDiscount / 100);

                                const finalDiscountMultiplier = 1 - quoteDiscount / 100;
                                const discountedPriceHTNum = priceAfterProductDiscountHT * finalDiscountMultiplier;
                                const discountedPriceHT = discountedPriceHTNum.toFixed(2);
                                const discountedPriceTTC = (priceAfterProductDiscountTTC * finalDiscountMultiplier).toFixed(2);

                                const hasAnyDiscount = productDiscount > 0 || quoteDiscount > 0;
                                const displayValueInInput = hasAnyDiscount ? discountedPriceHTNum : unitPriceHT;

                                const displayProductName = isProductLine ? (entity.designation || entity.product?.name || "Produit") : (entity.product?.name || "Service");

                                return (
                                    <div
                                        key={index}
                                        className="grid grid-cols-[18%_12%_8%_12%_12%_12%_12%_5%] items-center py-2 px-4 rounded-lg bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-0"
                                    >
                                        {/* Product Info */}
                                        <div className="flex items-center gap-2">
                                            <div>
                                            <div className="flex items-center gap-2">
            {entity.product?.discount > 0 && (
              <p className="inline-flex items-center gap-0.5 text-[9px] bg-[#FFD700] rounded-[12px] px-1 py-0.5 font-bold text-[#043F88]">
                <img src={discountIcon} alt="discount" className="w-2 h-2" />
                <span>{entity.product?.discount}%</span>
              </p>
            )}
          </div>
                                                <p className="text-[#6B78F8] font-semibold text-[16px]">
                                                    {displayProductName}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Prix HT (editable only for free lines) */}
                                        <div className="text-center flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuHT`).toUpperCase()}</p>
                                            {!entity.product ? (
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={editingPrice?.index === index
                                                    ? editingPrice.value
                                                    : (displayValueInInput != null && displayValueInInput !== '' ? Number(displayValueInInput).toFixed(2) : '')}
                                                onFocus={(e) => {
                                                    const formatted = displayValueInInput != null && displayValueInInput !== '' ? Number(displayValueInInput).toFixed(2) : '';
                                                    setEditingPrice({ index, value: formatted });
                                                    setTimeout(() => e.target.select(), 0);
                                                }}
                                                onChange={(e) => {
                                                    if (editingPrice?.index === index) {
                                                        setEditingPrice({ index, value: e.target.value.replace(',', '.') });
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (editingPrice?.index !== index) return;
                                                    if (editingPrice.value.trim() === '') {
                                                        setEditingPrice(null);
                                                        return;
                                                    }
                                                    handleUnitPriceChange(
                                                        index,
                                                        editingPrice.value,
                                                        hasAnyDiscount ? { productDiscount, quoteDiscount } : null
                                                    );
                                                    setEditingPrice(null);
                                                }}
                                                className="w-full max-w-[100px] text-center text-[#043F88] font-bold text-lg border border-gray-200 rounded-lg px-2 py-1 focus:border-[#043F88] focus:outline-none"
                                            />
                                            ) : (
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {displayValueInInput != null && displayValueInInput !== '' ? Number(displayValueInInput).toFixed(2) : '—'}
                                            </p>
                                            )}
                                            {hasAnyDiscount && (
                                                <p className="text-[#ACAFC6] text-sm line-through mt-0.5">
                                                    {Number(unitPriceHT).toFixed(2)}
                                                </p>
                                            )}
                                        </div>

                                        {/* TVA */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">TVA (%)</p>
                                            <p className="text-[#043F88] font-bold text-lg">{displayVatRate}%</p>
                                        </div>

                                        {/* Prix TTC */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuTTC`).toUpperCase()}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(discountedPriceTTC)}
                                            </p>
                                            {hasAnyDiscount && (
                                                <p className="text-[#ACAFC6] text-sm line-through inline-flex items-baseline">
                                                    {formatAmount(unitPriceTTC)}
                                                </p>
                                            )}
                                        </div>

                                                                                {/* Remise */}
                                                                                <div className="flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.remise%`)}</p>
                                            <div className="flex items-stretch rounded-[12px] border border-[#fea26f] bg-[#fffaf8] divide-x divide-[#fea26f]">
                                                <button
                                                    type="button"
                                                    onClick={() => handleDiscountChange(index, -0.1)}
                                                    className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                >
                                                    <img src={minusY} alt="Minus" className="w-4 h-4" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center text-[#38485C] font-semibold text-[14px]">
                                                    {entity.discount?.toFixed(1) || "0.0"}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDiscountChange(index, 0.1)}
                                                    className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                >
                                                    <img src={plusY} alt="Plus" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>


                                        {/* Quantité */}
                                        <div className="flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.quantity`).toUpperCase()}</p>
                                            <div className="flex items-stretch rounded-[12px] border border-[#6C78F8] bg-[#6C78F858] divide-x divide-[#6C78F8]">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const currentQuantity = listEntities[index].quantity;
                                                        const newQuantity = Math.max(1, currentQuantity - 1);
                                                        const newEntities = [...listEntities];
                                                        newEntities[index] = {
                                                            ...newEntities[index],
                                                            quantity: newQuantity
                                                        };
                                                        setListEntities(newEntities);
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                >
                                                    <img src={minus} alt="Minus" className="w-4 h-4" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center font-semibold text-[#38485C] text-[14px]">
                                                    {entity.quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newEntities = [...listEntities];
                                                        newEntities[index] = {
                                                            ...newEntities[index],
                                                            quantity: newEntities[index].quantity + 1
                                                        };
                                                        setListEntities(newEntities);
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                >
                                                    <img src={plus} alt="Plus" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>


                                        {/* Prix Total */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.totalPrice`)}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(entity.quantity * discountedPriceTTC)}
                                            </p>
                                            {hasAnyDiscount && (
                                                <p className="text-[#ACAFC6] text-sm line-through inline-flex items-baseline">
                                                    {formatAmount(entity.quantity * unitPriceTTC)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Delete Icon */}
                                        <div className="flex justify-center">
                                            <button
                                                type="button"
                                                onClick={() => handleTrashClick(index)}
                                                className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                            >
                                                <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                            </button>
                                        </div>

                                        </div>
                                    );
                                })}
                        </div>
                    )}


                </form>
            )}
                </div>

                {/* Totals and Footer */}
                <div className="pl-6 pr-6 pt-0 pb-6 ">
                    <div className="flex justify-between items-center">
                        <div className="flex text-left">
                            <p className="text-[#231E29] text-[16px] font-medium">
                                ACOMPTE:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(formValues.advancePayment) || formatAmount("0.00")}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL HT:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalHT)}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL TTC:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalTTC)}
                            </p>
                        </div>
                        <div className="space-x-4">
                            <SaveCancel
                                onSaveClick={handleSubmit}
                                onCancelClick={onCancel}
                                loading={submitting}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />
        </div>
    );
};

export default QuoteForm;